import React from 'react';
import {
  Grid,
  TextField,
  Autocomplete,
  Typography,
  Skeleton,
} from '@mui/material';
import LeadAdditionalDetailsSection from './LeadAdditionalDetailsSection';
import {
  capitalizeWords,
  isFollowUpStatusSelected,
  isValidEmail,
} from './leadFormHelpers';

const renderSkeletonField = () => (
  <Skeleton variant="rectangular" width="100%" height={40} animation="wave" />
);

const renderSkeletonSelect = () => (
  <Skeleton variant="rectangular" width="100%" height={56} animation="wave" />
);

export default function LeadEditForm({
  formData,
  setFormData,
  errors,
  setErrors,
  loading,
  isDisabled,
  statusOptions,
  sourceOptions,
  brandOptions,
  countryOptions,
  isCreateMode,
  leadId,
  assignees,
  attachmentsHydrate,
  handleChange,
  handlePhoneChange,
  hideStatus,
}) {
  const handleStatusChange = (event, newValue) => {
    const newStatusId = newValue ? newValue.id : '';
    const isFollowUp = isFollowUpStatusSelected(statusOptions, newStatusId);
    setFormData((prev) => ({
      ...prev,
      status_id: newStatusId,
      ...(!isFollowUp ? { follow_up_date_time: '' } : {}),
    }));
    if (errors.status_id || errors.follow_up_date_time) {
      setErrors((prev) => ({
        ...prev,
        status_id: '',
        ...(!isFollowUp ? { follow_up_date_time: '' } : {}),
      }));
    }
  };

  const handleEmailBlur = (field) => () => {
    const value = formData[field];
    if (value && !isValidEmail(value)) {
      setErrors((prev) => ({ ...prev, [field]: 'Email is invalid' }));
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        {loading ? (
          renderSkeletonField()
        ) : (
          <TextField
            label="DBA Name"
            fullWidth
            required
            placeholder="Enter DBA name (Doing Business As)"
            value={formData.dba_name}
            onChange={handleChange('dba_name')}
            error={!!errors.dba_name}
            helperText={errors.dba_name}
            disabled={isDisabled}
          />
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        {loading ? (
          renderSkeletonField()
        ) : (
          <TextField
            label="Customer Name"
            fullWidth
            required
            placeholder="Customer Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            disabled={isDisabled}
          />
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        {loading ? (
          renderSkeletonField()
        ) : (
          <TextField
            fullWidth
            label="Email"
            type="email"
            required
            placeholder="name@example.com"
            value={formData.email}
            onChange={handleChange('email')}
            onBlur={handleEmailBlur('email')}
            error={!!errors.email}
            helperText={errors.email}
            disabled={isDisabled}
          />
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        {loading ? (
          renderSkeletonField()
        ) : (
          <TextField
            fullWidth
            label="Phone"
            type="tel"
            required
            placeholder="1234567890"
            value={formData.phone}
            onChange={handlePhoneChange}
            error={!!errors.phone}
            helperText={errors.phone}
            disabled={isDisabled}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 15 }}
          />
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        {loading ? (
          renderSkeletonSelect()
        ) : (
          <Autocomplete
            options={countryOptions}
            getOptionLabel={(option) => option.name || ''}
            value={countryOptions.find((c) => c.id === formData.country_id) || null}
            onChange={(event, newValue) => {
              setFormData((prev) => ({ ...prev, country_id: newValue ? newValue.id : '' }));
            }}
            disabled={isDisabled}
            renderInput={(params) => <TextField {...params} label="Select country" fullWidth />}
          />
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        {loading ? (
          renderSkeletonSelect()
        ) : (
          <Autocomplete
            options={sourceOptions}
            getOptionLabel={(option) => capitalizeWords(option.name || '')}
            value={sourceOptions.find((s) => s.id === formData.source_id) || null}
            onChange={(event, newValue) => {
              setFormData((prev) => ({ ...prev, source_id: newValue ? newValue.id : '' }));
              if (errors.source_id) setErrors((prev) => ({ ...prev, source_id: '' }));
            }}
            disabled={isDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select source"
                required
                error={!!errors.source_id}
                helperText={errors.source_id}
                fullWidth
              />
            )}
          />
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        {loading ? (
          renderSkeletonField()
        ) : (
          <TextField
            fullWidth
            label="Address"
            placeholder="Enter complete address"
            value={formData.address}
            onChange={handleChange('address')}
            disabled={isDisabled}
            multiline
            rows={3}
          />
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        {loading ? (
          renderSkeletonField()
        ) : (
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            placeholder="Enter description"
            value={formData.description}
            onChange={handleChange('description')}
            disabled={isDisabled}
          />
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        {loading ? (
          renderSkeletonSelect()
        ) : (
          <Autocomplete
            options={brandOptions}
            getOptionLabel={(option) => capitalizeWords(option.name || '')}
            value={brandOptions.find((b) => b.id === formData.brand_id) || null}
            onChange={(event, newValue) => {
              setFormData((prev) => ({ ...prev, brand_id: newValue ? newValue.id : '' }));
              if (errors.brand_id) setErrors((prev) => ({ ...prev, brand_id: '' }));
            }}
            disabled={isDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select brand"
                required
                error={!!errors.brand_id}
                helperText={errors.brand_id}
                fullWidth
              />
            )}
          />
        )}
      </Grid>

      {!hideStatus && (
        <Grid item xs={12} md={6}>
          {loading ? (
            renderSkeletonSelect()
          ) : (
            <Autocomplete
              options={statusOptions}
              getOptionLabel={(option) => capitalizeWords(option.name || '')}
              value={statusOptions.find((s) => s.id === formData.status_id) || null}
              onChange={handleStatusChange}
              disabled={isDisabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select status"
                  required
                  error={!!errors.status_id}
                  helperText={errors.status_id}
                  fullWidth
                />
              )}
            />
          )}
        </Grid>
      )}

      {isFollowUpStatusSelected(statusOptions, formData.status_id) && (
        <Grid item xs={12} md={6}>
          {loading ? (
            renderSkeletonSelect()
          ) : (
            <TextField
              fullWidth
              label="Follow-up date & time"
              type="datetime-local"
              required
              name="follow_up_date_time"
              value={formData.follow_up_date_time}
              onChange={handleChange('follow_up_date_time')}
              disabled={isDisabled}
              error={!!errors.follow_up_date_time}
              helperText={errors.follow_up_date_time}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </Grid>
      )}

      <Grid item xs={12} md={6}>
        {loading ? (
          renderSkeletonField()
        ) : (
          <TextField
            fullWidth
            label="Average tickets"
            placeholder="e.g. $250 or typical ticket size"
            value={formData.average_tickets ?? ''}
            onChange={handleChange('average_tickets')}
            disabled={isDisabled}
          />
        )}
      </Grid>

      <Grid item xs={12}>
        <LeadAdditionalDetailsSection
          formData={formData}
          setFormData={setFormData}
          disabled={isDisabled}
          isViewMode={false}
          leadId={leadId}
          assignees={assignees}
          attachmentsHydrateToken={attachmentsHydrate}
          defaultOpen
          hideAttachments
          showOfferServices
        />
      </Grid>
    </Grid>
  );
}
